/**
 * Unified Customers Page with Tabs
 * Notion-style table view with inline editing
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Users, AlertCircle, RefreshCw, Car, List, LayoutGrid } from 'lucide-react';
import {
  Button,
  EmptyState,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui';
import { CustomerForm, CustomerCard, CustomerCardSkeleton } from '../../components/features/customers';
import { CustomersTable, type CustomerTableItem } from '../../components/customers/CustomersTable';
import { VehiclesTable } from '../../components/vehicles/VehiclesTable';
import { VehicleCard, VehicleCardSkeleton } from '../../components/vehicles/VehicleCard';
import { cn } from '../../lib/utils';
import { iconSizes } from '../../lib/styles';
import { useToast } from '../../components/ui/toast';
import { useToast as useToastContext } from '../../contexts/ToastContext';
import {
  useCustomerList,
  useCreateCustomer,
  useUpdateCustomer,
  useDeleteCustomer,
  type Customer,
} from '../../hooks/useCustomers';
import { ApiError } from '../../lib/api';
import { useVehicleListPage } from '../../hooks/useVehicles';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

/**
 * Transform API customer to table format
 */
function toTableCustomer(customer: Customer): CustomerTableItem {
  return {
    id: customer.id,
    name: customer.name,
    email: customer.email,
    phone: customer.phone,
    dni: customer.dni,
    // Use vehicleCount from API (calculated in backend) or fallback to vehicles array length
    vehicleCount: (customer as Customer & { vehicleCount?: number }).vehicleCount ?? customer.vehicles?.length ?? 0,
    createdAt: customer.createdAt,
    updatedAt: customer.updatedAt,
  };
}

/**
 * Customers Tab Content - Table or Grid view
 */
function CustomersTabContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [displayMode, setDisplayMode] = useState<'table' | 'cards'>('table');
  const limit = 20;

  const { addToast } = useToast();

  // Debounce search query
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Fetch customers from API
  const { data, isLoading, isError, error, refetch } = useCustomerList({
    q: debouncedSearch || undefined,
    limit,
    offset,
  });

  // Mutations
  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();
  const deleteMutation = useDeleteCustomer();

  // Get customers from response
  const customers = useMemo(() => {
    return (data?.items || []).map(toTableCustomer);
  }, [data?.items]);

  const total = data?.items.length || 0;

  // Handle inline field update
  const handleFieldUpdate = useCallback(async (customerId: string, field: string, value: string) => {
    try {
      await updateMutation.mutateAsync({
        id: customerId,
        data: { [field]: value },
      });
      addToast({
        variant: 'success',
        title: 'Actualizado',
      });
    } catch (err: unknown) {
      const error = err as { message?: string };
      addToast({
        variant: 'destructive',
        title: 'Error al actualizar',
        description: error.message || 'Intente nuevamente',
      });
      throw err;
    }
  }, [updateMutation, addToast]);

  const handleUpdateError = useCallback((_customerId: string, field: string, err: Error) => {
    console.error(`Error updating ${field}:`, err);
  }, []);

  const handleCreateCustomer = async (formData: {
    name: string;
    email: string;
    phone: string;
  }) => {
    try {
      await createMutation.mutateAsync(formData);
      addToast({
        variant: 'success',
        title: 'Cliente creado correctamente',
      });
      setFormOpen(false);
    } catch (err: unknown) {
      const error = err as { message?: string };
      addToast({
        variant: 'destructive',
        title: 'Error al crear cliente',
        description: error.message || 'Intente nuevamente',
      });
    }
  };

  const handleEditCustomer = (tableCustomer: CustomerTableItem) => {
    const fullCustomer = data?.items.find(c => c.id === tableCustomer.id);
    if (fullCustomer) {
      setEditingCustomer(fullCustomer);
      setFormOpen(true);
    }
  };

  const handleUpdateCustomer = async (formData: {
    name: string;
    email: string;
    phone: string;
  }) => {
    if (!editingCustomer) return;

    try {
      await updateMutation.mutateAsync({
        id: editingCustomer.id,
        data: formData,
      });
      addToast({
        variant: 'success',
        title: 'Cliente actualizado correctamente',
      });
      setFormOpen(false);
      setEditingCustomer(null);
    } catch (err: unknown) {
      const error = err as { message?: string };
      addToast({
        variant: 'destructive',
        title: 'Error al actualizar cliente',
        description: error.message || 'Intente nuevamente',
      });
    }
  };

  const handleDeleteCustomer = async (tableCustomer: CustomerTableItem) => {
    if (!confirm('Esta seguro de que desea eliminar este cliente?')) return;

    try {
      await deleteMutation.mutateAsync(tableCustomer.id);
      addToast({
        variant: 'success',
        title: 'Cliente eliminado',
      });
    } catch (err: unknown) {
      if (err instanceof ApiError && err.code === 'CUSTOMER_HAS_RECORDS') {
        const details = err.details as { bookings?: number; jobs?: number } | undefined;
        const bookings = details?.bookings || 0;
        const jobs = details?.jobs || 0;

        let description = 'El cliente tiene ';
        const parts: string[] = [];
        if (bookings > 0) parts.push(`${bookings} reserva${bookings > 1 ? 's' : ''}`);
        if (jobs > 0) parts.push(`${jobs} trabajo${jobs > 1 ? 's' : ''}`);
        description += parts.join(' y ') + '. ';
        description += 'Elimine estos registros primero o use la opcion de anonimizacion RGPD.';

        addToast({
          variant: 'destructive',
          title: 'No se puede eliminar el cliente',
          description,
        });
      } else {
        const error = err as { message?: string };
        addToast({
          variant: 'destructive',
          title: 'Error al eliminar cliente',
          description: error.message || 'Intente nuevamente',
        });
      }
    }
  };

  const handleOpenNewForm = () => {
    setEditingCustomer(null);
    setFormOpen(true);
  };

  const handleSearchChange = (search: string) => {
    setSearchQuery(search);
    setOffset(0);
  };

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <AlertCircle className="h-8 w-8 text-red-500 mb-3" />
        <h3 className="text-sm font-medium text-red-800 mb-1">Error al cargar clientes</h3>
        <p className="text-xs text-red-600 mb-3 max-w-xs">
          {(error as Error)?.message || 'No se pudieron cargar los clientes'}
        </p>
        <Button onClick={() => refetch()} variant="secondary" size="sm">
          <RefreshCw className="h-3 w-3 mr-1.5" />
          Reintentar
        </Button>
      </div>
    );
  }

  // Empty state (no customers and no search)
  if (!isLoading && customers.length === 0 && !searchQuery) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button onClick={handleOpenNewForm} size="sm">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Nuevo
          </Button>
        </div>
        <EmptyState
          icon={Users}
          title="Sin clientes"
          description="Aun no tienes clientes registrados. Crea el primero para empezar."
          actionLabel="Nuevo Cliente"
          onAction={handleOpenNewForm}
        />
        <CustomerForm
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open);
            if (!open) setEditingCustomer(null);
          }}
          customer={null}
          onSubmit={handleCreateCustomer}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with New Button and View Toggle */}
      <div className="flex items-center justify-between">
        {/* View Toggle */}
        <div className="flex items-center gap-0.5 bg-gray-100 p-0.5 rounded-md">
          <button
            onClick={() => setDisplayMode('table')}
            className={cn('p-1.5 rounded transition-colors', displayMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700')}
            title="Vista tabla"
          >
            <List className={iconSizes.sm} />
          </button>
          <button
            onClick={() => setDisplayMode('cards')}
            className={cn('p-1.5 rounded transition-colors', displayMode === 'cards' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700')}
            title="Vista tarjetas"
          >
            <LayoutGrid className={iconSizes.sm} />
          </button>
        </div>

        <Button onClick={handleOpenNewForm} size="sm">
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Nuevo
        </Button>
      </div>

      {/* Table View */}
      {displayMode === 'table' && (
        <CustomersTable
          customers={customers}
          isLoading={isLoading}
          error={error as Error | null}
          total={total}
          limit={limit}
          offset={offset}
          search={searchQuery}
          onSearchChange={handleSearchChange}
          onPageChange={setOffset}
          onFieldUpdate={handleFieldUpdate}
          onUpdateError={handleUpdateError}
          onEdit={handleEditCustomer}
          onDelete={handleDeleteCustomer}
        />
      )}

      {/* Cards View */}
      {displayMode === 'cards' && (
        <div className="space-y-4">
          {/* Search for cards view */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Buscar cliente..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <CustomerCardSkeleton key={i} />
              ))}
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              {searchQuery ? 'No se encontraron clientes' : 'Sin clientes'}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {customers.map((customer) => (
                <CustomerCard
                  key={customer.id}
                  customer={{
                    id: customer.id,
                    name: customer.name,
                    email: customer.email || '',
                    phone: customer.phone || '',
                    vehicleCount: customer.vehicleCount,
                    status: 'active',
                  }}
                  onEdit={() => handleEditCustomer(customer)}
                  onDelete={() => handleDeleteCustomer(customer)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Customer Form Modal */}
      <CustomerForm
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingCustomer(null);
        }}
        customer={editingCustomer ? {
          id: editingCustomer.id,
          name: editingCustomer.name,
          email: editingCustomer.email,
          phone: editingCustomer.phone,
          vehicleCount: 0,
          status: 'active',
          createdAt: new Date(editingCustomer.createdAt * 1000).toISOString(),
        } : null}
        onSubmit={editingCustomer ? handleUpdateCustomer : handleCreateCustomer}
      />
    </div>
  );
}

/**
 * Vehicles Tab Content
 */
function VehiclesTabContent() {
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToastContext();
  const [displayMode, setDisplayMode] = useState<'table' | 'cards'>('table');

  const {
    vehicles,
    total,
    isLoading,
    error,
    search,
    sortBy,
    sortDir,
    offset,
    limit,
    onSearchChange,
    onSortChange,
    onPageChange,
    onFieldUpdate,
  } = useVehicleListPage();

  // Handle update errors
  const handleUpdateError = (_vehicleId: string, field: string, err: Error) => {
    showError(`Error al actualizar ${field}: ${err.message}`);
  };

  // Handle successful update
  const handleFieldUpdate = async (
    vehicleId: string,
    field: string,
    value: string,
    version: number
  ) => {
    const result = await onFieldUpdate(vehicleId, field, value, version);
    showSuccess('Vehículo actualizado');
    return result;
  };

  return (
    <div className="space-y-4">
      {/* Header with New Button and View Toggle */}
      <div className="flex items-center justify-between">
        {/* View Toggle */}
        <div className="flex items-center gap-0.5 bg-gray-100 p-0.5 rounded-md">
          <button
            onClick={() => setDisplayMode('table')}
            className={cn('p-1.5 rounded transition-colors', displayMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700')}
            title="Vista tabla"
          >
            <List className={iconSizes.sm} />
          </button>
          <button
            onClick={() => setDisplayMode('cards')}
            className={cn('p-1.5 rounded transition-colors', displayMode === 'cards' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700')}
            title="Vista tarjetas"
          >
            <LayoutGrid className={iconSizes.sm} />
          </button>
        </div>

        <Button onClick={() => navigate('/vehicles/new')} size="sm">
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Nuevo
        </Button>
      </div>

      {/* Table View */}
      {displayMode === 'table' && (
        <VehiclesTable
          vehicles={vehicles}
          isLoading={isLoading}
          error={error}
          total={total}
          limit={limit}
          offset={offset}
          search={search}
          sortBy={sortBy}
          sortDir={sortDir}
          onSearchChange={onSearchChange}
          onPageChange={onPageChange}
          onSortChange={onSortChange}
          onFieldUpdate={handleFieldUpdate}
          onUpdateError={handleUpdateError}
        />
      )}

      {/* Cards View */}
      {displayMode === 'cards' && (
        <div className="space-y-4">
          {/* Search for cards view */}
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar vehiculo..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <VehicleCardSkeleton key={i} />
              ))}
            </div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              {search ? 'No se encontraron vehiculos' : 'Sin vehiculos'}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {vehicles.map((vehicle) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={{
                    id: vehicle.id,
                    registration: vehicle.registration,
                    make: vehicle.make,
                    model: vehicle.model,
                    year: vehicle.year ?? null,
                    color: vehicle.color ?? null,
                    customerName: vehicle.customer?.name,
                    customerId: vehicle.customerId,
                  }}
                  onEdit={() => navigate(`/vehicles/${vehicle.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Main Customers Page with Tabs
 */
export function CustomersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'customers';

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Clientes y Vehiculos</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Gestiona clientes y sus vehiculos
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={defaultTab} onValueChange={handleTabChange}>
        <TabsList className="h-8">
          <TabsTrigger value="customers" className="flex items-center gap-1.5 text-xs h-7 px-3">
            <Users className="h-3.5 w-3.5" />
            Clientes
          </TabsTrigger>
          <TabsTrigger value="vehicles" className="flex items-center gap-1.5 text-xs h-7 px-3">
            <Car className="h-3.5 w-3.5" />
            Vehiculos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="mt-4">
          <CustomersTabContent />
        </TabsContent>

        <TabsContent value="vehicles" className="mt-4">
          <VehiclesTabContent />
        </TabsContent>
      </Tabs>
    </div>
  );
}
